import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authAPI } from '../../services/api';

interface User {
  id: string;
  email: string;
  fullName: string;
  role: string;
  staffId?:string|null;
}

interface Tenant {
  id: string;
  name: string;
  subdomain?: string;
  features?: Record<string, boolean>;
  logoUrl?: string | null;
  businessType?: string;
  drivingMode?: 'auto_moto' | 'auto' | 'moto';
}

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  token: string | null;
  loadRequestId?: string;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  tenant: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: !!localStorage.getItem('token'),
  error: null,
};

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      const { user, tenant, tokens } = response.data;
      
      localStorage.setItem('token', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      
      return { user, tenant, token: tokens.accessToken };
    } catch (error: any) {
      let message = 'Eroare la autentificare';
      
      if (error.response?.data?.error) {
        // Handle both string and object errors
        if (typeof error.response.data.error === 'string') {
          message = error.response.data.error;
        } else if (error.response.data.error.message) {
          message = error.response.data.error.message;
        }
      } else if (error.message) {
        message = error.message;
      }
      
      return rejectWithValue(message);
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (data: {
    tenantName: string;
    subdomain: string;
    email: string;
    password: string;
    fullName: string;
    businessType?: string;
    phone?: string;
    address?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(data);
      const { user, tenant, tokens } = response.data;
      
      localStorage.setItem('token', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      
      return { user, tenant, token: tokens.accessToken };
    } catch (error: any) {
      let message = 'Eroare la înregistrare';
      
      if (error.response?.data?.error) {
        // Handle both string and object errors
        if (typeof error.response.data.error === 'string') {
          message = error.response.data.error;
        } else if (error.response.data.error.message) {
          message = error.response.data.error.message;
        }
      } else if (error.message) {
        message = error.message;
      }
      
      return rejectWithValue(message);
    }
  }
);

export const loadUser = createAsyncThunk(
  'auth/loadUser',
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = (getState() as any).auth.token;

      
      if (!token) {

        return rejectWithValue('Nu există token');
      }

      const response = await authAPI.getProfile();

      
      const { user, tenant } = response.data;

      
      return { user, tenant };
    } catch (error: any) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      
      let message = 'Eroare la încărcarea utilizatorului';
      
      if (error.response?.data?.error) {
        if (typeof error.response.data.error === 'string') {
          message = error.response.data.error;
        } else if (error.response.data.error.message) {
          message = error.response.data.error.message;
        }
      } else if (error.message) {
        message = error.message;
      }
      
      return rejectWithValue(message);
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data: {
    fullName?: string;
    currentPassword?: string;
    newPassword?: string;
  }, { rejectWithValue }) => {
    try {
      const response = await authAPI.updateProfile(data);
      return response.data.user;
    } catch (error: any) {
      let message = 'Eroare la actualizarea profilului';
      
      if (error.response?.data?.error) {
        if (typeof error.response.data.error === 'string') {
          message = error.response.data.error;
        } else if (error.response.data.error.message) {
          message = error.response.data.error.message;
        }
      } else if (error.message) {
        message = error.message;
      }
      
      return rejectWithValue(message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    updateTenantLogo: (state, action: PayloadAction<{ tenantId: string; logoUrl: string | null }>) => {
      if (state.tenant?.id === action.payload.tenantId) state.tenant.logoUrl = action.payload.logoUrl;
    },
    syncSession: (state, action) => { state.user = action.payload.user; state.tenant = action.payload.tenant; },
    logout: (state) => {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      state.user = null;
      state.tenant = null;
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      localStorage.setItem('token', action.payload);
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.tenant = action.payload.tenant;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    // Register
    builder
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.tenant = action.payload.tenant;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    // Load user
    builder
      .addCase(loadUser.pending, (state, action) => {
        state.loadRequestId = action.meta.requestId;
        state.loading = true;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        if (state.loadRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.user = action.payload.user;
        state.tenant = action.payload.tenant;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(loadUser.rejected, (state, action) => {
        if (state.loadRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.user = null;
        state.tenant = null;
        state.token = null;
      });

    // Update profile
    builder
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = { ...state.user!, ...action.payload };
        state.error = null;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearError, setToken, syncSession, updateTenantLogo } = authSlice.actions;
export default authSlice.reducer;
